import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './Wishlist.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function Wishlist({ factFictionMode, navigateTo }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [wantToRead, setWantToRead] = useState([]);
  const [wishlistOwned, setWishlistOwned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [wtrData, wlData] = await Promise.all([
        apiFetchJson(instance, '/api/userbookstatus?status=WantToRead').catch(() => null),
        apiFetchJson(instance, '/api/userlibrary?ownership=Wishlist').catch(() => null),
      ]);
      if (wtrData) {
        setWantToRead(unwrap(wtrData));
      }
      if (wlData) {
        setWishlistOwned(unwrap(wlData));
      }
    } catch {
      setError('Failed to load wishlist.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markAsRead = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`read-${bookId}`]: 'loading' }));
    try {
      await apiFetchJson(instance, `/api/userbookstatus/${bookId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Finished' }),
      });
      setActionStatus(prev => ({ ...prev, [`read-${bookId}`]: 'success' }));
      setTimeout(() => {
        setWantToRead(prev => prev.filter(e => (e.bookId || e.book?.id) !== bookId));
      }, 800);
    } catch {
      setActionStatus(prev => ({ ...prev, [`read-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const addToLibrary = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userlibrary', {
        method: 'POST',
        body: JSON.stringify({ bookId, ownership: 'Owned' }),
      });
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: (res.ok || res.status === 409) ? 'success' : 'error' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const removeFromWishlist = useCallback(async (entryId, bookId, source) => {
    setActionStatus(prev => ({ ...prev, [`rm-${bookId}`]: 'loading' }));
    try {
      if (source === 'library') {
        await apiFetchJson(instance, `/api/userlibrary/${entryId}`, { method: 'DELETE' });
      } else {
        await apiFetchJson(instance, `/api/userbookstatus/${bookId}`, { method: 'DELETE' });
      }
      setActionStatus(prev => ({ ...prev, [`rm-${bookId}`]: 'success' }));
      setTimeout(() => {
        if (source === 'library') {
          setWishlistOwned(prev => prev.filter(e => e.id !== entryId));
        } else {
          setWantToRead(prev => prev.filter(e => (e.bookId || e.book?.id) !== bookId));
        }
      }, 500);
    } catch {
      setActionStatus(prev => ({ ...prev, [`rm-${bookId}`]: 'error' }));
    }
  }, [instance]);

  // Merge and deduplicate
  const allItems = [];
  const seenBookIds = new Set();

  wantToRead.forEach(entry => {
    const bookId = entry.bookId || entry.book?.id;
    if (bookId && !seenBookIds.has(bookId)) {
      seenBookIds.add(bookId);
      allItems.push({
        bookId,
        title: entry.book?.title || 'Unknown',
        author: entry.book?.author || '',
        coverUrl: entry.book?.coverImageUrl,
        description: entry.book?.description,
        pageCount: entry.book?.pageCount,
        bookType: entry.book?.bookType,
        source: 'wantToRead',
        entryId: entry.id,
        addedAt: entry.createdAt || entry.updatedAt,
      });
    }
  });

  wishlistOwned.forEach(entry => {
    const bookId = entry.bookId || entry.book?.id;
    if (bookId && !seenBookIds.has(bookId)) {
      seenBookIds.add(bookId);
      allItems.push({
        bookId,
        title: entry.book?.title || 'Unknown',
        author: entry.book?.author || '',
        coverUrl: entry.book?.coverImageUrl,
        description: entry.book?.description,
        pageCount: entry.book?.pageCount,
        bookType: entry.book?.bookType,
        source: 'library',
        entryId: entry.id,
        addedAt: entry.createdAt || entry.updatedAt,
      });
    }
  });

  const filtered = activeFilter === 'all' ? allItems
    : activeFilter === 'wantToRead' ? allItems.filter(i => i.source === 'wantToRead')
    : allItems.filter(i => i.source === 'library');

  return (
    <div className="wishlist-container">
      <div className="wishlist-header">
        <h2 className="wishlist-title">🎁 {t('bookClub.wishlist.title')}</h2>
        <p className="wishlist-subtitle">{t('bookClub.wishlist.subtitle', { count: allItems.length })}</p>
      </div>

      <div className="wishlist-filters">
        <button
          className={`wishlist-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          {t('bookClub.wishlist.all')} ({allItems.length})
        </button>
        <button
          className={`wishlist-filter-btn ${activeFilter === 'wantToRead' ? 'active' : ''}`}
          onClick={() => setActiveFilter('wantToRead')}
        >
          📌 {t('bookClub.wishlist.wantToRead')} ({wantToRead.length})
        </button>
        <button
          className={`wishlist-filter-btn ${activeFilter === 'library' ? 'active' : ''}`}
          onClick={() => setActiveFilter('library')}
        >
          🎁 {t('bookClub.wishlist.buyList')} ({wishlistOwned.length})
        </button>
      </div>

      {loading && (
        <div className="wishlist-loading">
          <div className="wishlist-spinner" />
          <p>{t('bookClub.wishlist.loading')}</p>
        </div>
      )}

      {error && <p className="wishlist-error">{error}</p>}

      {!loading && filtered.length === 0 && (
        <div className="wishlist-empty">
          <span className="wishlist-empty-icon">📭</span>
          <p>{t('bookClub.wishlist.empty')}</p>
          <p>{t('bookClub.wishlist.emptyHint')}</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="wishlist-grid">
          {filtered.map(item => (
            <div
              key={item.bookId}
              className={`wishlist-card ${actionStatus[`rm-${item.bookId}`] === 'success' ? 'wishlist-card--removed' : ''}`}
            >
              <BookCover
                src={item.coverUrl}
                alt={item.title}
                className="wishlist-card-cover"
                emoji={item.bookType === 'fiction' ? '📖' : '📚'}
              />
              <div className="wishlist-card-info">
                <h3 className="wishlist-card-title" title={item.title}>{item.title}</h3>
                <p className="wishlist-card-author">{item.author}</p>
                <span className={`wishlist-card-badge wishlist-badge--${item.source}`}>
                  {item.source === 'wantToRead' ? '📌 ' + t('bookClub.wishlist.wantToRead') : '🎁 ' + t('bookClub.wishlist.buyList')}
                </span>
                {item.description && (
                  <p className="wishlist-card-desc">{item.description.length > 120 ? item.description.slice(0, 120) + '…' : item.description}</p>
                )}
              </div>
              <div className="wishlist-card-actions">
                <button
                  className={`wishlist-btn wishlist-btn--bought ${actionStatus[`lib-${item.bookId}`] || ''}`}
                  onClick={() => addToLibrary(item.bookId)}
                  disabled={actionStatus[`lib-${item.bookId}`] === 'loading' || actionStatus[`lib-${item.bookId}`] === 'success'}
                  title="I bought it!"
                >
                  {actionStatus[`lib-${item.bookId}`] === 'loading' ? '⏳' :
                   actionStatus[`lib-${item.bookId}`] === 'success' ? '✓ ' + t('bookClub.wishlist.owned') : '🛒 ' + t('bookClub.wishlist.boughtIt')}
                </button>
                <button
                  className={`wishlist-btn wishlist-btn--read ${actionStatus[`read-${item.bookId}`] || ''}`}
                  onClick={() => markAsRead(item.bookId)}
                  disabled={actionStatus[`read-${item.bookId}`] === 'loading' || actionStatus[`read-${item.bookId}`] === 'success'}
                >
                  {actionStatus[`read-${item.bookId}`] === 'loading' ? '⏳' :
                   actionStatus[`read-${item.bookId}`] === 'success' ? '✓ ' + t('bookClub.wishlist.read') + '!' : '✓ ' + t('bookClub.wishlist.read')}
                </button>
                <button
                  className={`wishlist-btn wishlist-btn--remove ${actionStatus[`rm-${item.bookId}`] || ''}`}
                  onClick={() => removeFromWishlist(item.entryId, item.bookId, item.source)}
                  disabled={actionStatus[`rm-${item.bookId}`] === 'loading' || actionStatus[`rm-${item.bookId}`] === 'success'}
                  title="Remove from wishlist"
                >
                  {actionStatus[`rm-${item.bookId}`] === 'loading' ? '⏳' :
                   actionStatus[`rm-${item.bookId}`] === 'success' ? '✓ ' + t('bookClub.wishlist.removed') : '✕'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Wishlist;
